/** Create or edit a personal prompt (#1825). */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'
import { logError } from '@/lib/logger'
import { createPrompt, updatePrompt, type UserPrompt } from '@/services/prompt-library-service'

export function PromptEditorDialog({
  open,
  prompt,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  prompt: UserPrompt | null
  onOpenChange: (open: boolean) => void
  onSaved: (prompt: UserPrompt) => void
}) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setTitle(prompt?.title ?? '')
    setDescription(prompt?.description ?? '')
    setText(prompt?.prompt_text ?? '')
  }, [open, prompt])

  const save = async () => {
    setSaving(true)
    const input = { title, description: description || undefined, prompt_text: text }
    try {
      const saved = prompt ? await updatePrompt(prompt.id, input) : await createPrompt(input)
      if (!saved) throw new Error('Saving the prompt returned nothing')
      toast({ title: t('common.success'), description: t(prompt ? 'promptLibrary.updateSuccess' : 'promptLibrary.createSuccess') })
      onSaved(saved)
      onOpenChange(false)
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), { context: 'PromptEditorDialog.save' })
      toast({ title: t('common.error'), description: t('promptLibrary.errors.saveFailed'), variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(prompt ? 'promptLibrary.editPrompt' : 'promptLibrary.newPrompt')}</DialogTitle>
          <DialogDescription>{t('promptLibrary.editorHint')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label htmlFor="prompt-title" className="mb-1.5 block text-sm font-medium">{t('promptLibrary.titleLabel')} *</label>
            <Input id="prompt-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('promptLibrary.titlePlaceholder')} />
          </div>
          <div>
            <label htmlFor="prompt-description" className="mb-1.5 block text-sm font-medium">{t('promptLibrary.descriptionLabel')}</label>
            <Input id="prompt-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('promptLibrary.descriptionPlaceholder')} />
          </div>
          <div>
            <label htmlFor="prompt-text" className="mb-1.5 block text-sm font-medium">{t('promptLibrary.promptTextLabel')} *</label>
            <textarea
              id="prompt-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={t('promptLibrary.promptTextPlaceholder')}
              className="w-full resize-none rounded-lg border border-[var(--window-border-color)] bg-[var(--input-bg)] px-3 py-2 font-mono text-sm text-[var(--text-color)]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={save} disabled={!title.trim() || !text.trim() || saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
